'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type SelectOptionType = {
  value: string;
  label: string;
  disabled?: boolean;
  description?: string;
};

export type SelectContextType = {
  currentValue?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  onCloseAutoFocus?: (event: Event) => void;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onPointerDownOutside?: (event: CustomEvent<{ originalEvent: PointerEvent }>) => void;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  options: Map<string, SelectOptionType>;
  registerOption: (option: SelectOptionType) => void;
  unregisterOption: (value: string) => void;
  isControlled: boolean;
  getOptionByValue: (value: string) => SelectOptionType | undefined;
  getOptions: () => SelectOptionType[];
};

const SelectContext = createContext<SelectContextType>({
  options: new Map(),
  registerOption: () => undefined,
  unregisterOption: () => undefined,
  isControlled: false,
  getOptionByValue: () => undefined,
  onValueChange: () => undefined,
  onCloseAutoFocus: () => undefined,
  onEscapeKeyDown: () => undefined,
  onFocus: () => undefined,
  onBlur: () => undefined,
  onPointerDownOutside: () => undefined,
  getOptions: () => [],
});

export type SelectProviderProps = {
  children: React.ReactNode;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  onCloseAutoFocus?: (event: Event) => void;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onPointerDownOutside?: (event: CustomEvent<{ originalEvent: PointerEvent }>) => void;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
};

/**
 * Context provider that manages Select state and option registration.
 *
 * For Spellbook contributors: This provider is used internally by the `Select` component.
 * It manages controlled/uncontrolled state, tracks registered options, and provides
 * callbacks to child components. You should not need to use this directly — use
 * `Select` instead, which wraps this provider automatically.
 *
 * ### Context values provided
 * - `currentValue`: The currently selected value (controlled or internal)
 * - `options`: Map of registered options for lookup
 * - `registerOption` / `unregisterOption`: Option lifecycle management
 * - `getOptionByValue`: Retrieve option metadata by value
 * - `isControlled`: Whether the select is in controlled mode
 */
export const SelectProvider: React.FC<SelectProviderProps> = ({
  children,
  value,
  defaultValue,
  onValueChange,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onPointerDownOutside,
  onFocus,
  onBlur,
}) => {
  const [options, setOptions] = useState<Map<string, SelectOptionType>>(new Map());
  const [internalValue, setInternalValue] = useState<string>(defaultValue ?? '');

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const registerOption = useCallback((option: SelectOptionType) => {
    setOptions((prev) => {
      const newOptions = new Map(prev);
      newOptions.set(option.value, option);
      return newOptions;
    });
  }, []);

  const unregisterOption = useCallback((value: string) => {
    setOptions((prev) => {
      const newOptions = new Map(prev);
      newOptions.delete(value);
      return newOptions;
    });
  }, []);

  const getOptionByValue = useCallback(
    (value: string) => {
      return options.get(value);
    },
    [options],
  );

  const getOptions = useCallback(() => {
    return Array.from(options.values());
  }, [options]);

  const handleValueChange = useCallback(
    (newValue: string) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [isControlled, onValueChange],
  );

  const contextValue = useMemo<SelectContextType>(
    () => ({
      value,
      currentValue,
      defaultValue,
      onValueChange: handleValueChange,
      onCloseAutoFocus,
      onEscapeKeyDown,
      onPointerDownOutside,
      onFocus,
      onBlur,
      options,
      registerOption,
      unregisterOption,
      isControlled,
      getOptionByValue,
      getOptions,
    }),
    [
      value,
      currentValue,
      defaultValue,
      handleValueChange,
      options,
      registerOption,
      unregisterOption,
      isControlled,
      getOptionByValue,
      getOptions,
    ],
  );

  return <SelectContext.Provider value={contextValue}>{children}</SelectContext.Provider>;
};

export const useSelect = () => useContext(SelectContext);
